/**
 * @module @gzl10/nexus-backend
 * @description Express server with auto-generated CRUD, CASL authorization, and module system
 *
 * @exports start - Start the server with StartOptions
 * @exports stop - Graceful shutdown
 * @exports registerModule - Register a module manifest
 * @exports registerPlugin - Register a plugin with multiple modules
 * @exports BaseEntityService - Base class for entity services (extend for custom logic)
 * @exports CollectionService - Service for collection entities (full CRUD)
 * @exports TreeService - Service for hierarchical entities (CRUD + move)
 * @exports DagService - Service for multi-parent graph entities
 * @exports defineAbilityFor - CASL ability factory for permissions
 * @exports nexusEvents - Event emitter for server lifecycle and DB changes
 * @exports getIO - Get Socket.IO server instance for real-time features
 */

// Cargar .env PRIMERO - este import no tiene deps locales
import './env-loader.js'

// Package version
import pkg from '../package.json' with { type: 'json' }
export const version: string = pkg.version

// Server lifecycle
export { start, stop, restart, isRunning } from './http/start.js'
export type { NexusConfig, StartOptions } from './http/start.js'

// Core exports
export { db, getDb, destroyDb } from '@kernel/db/connection.js'
export { getDatabaseType } from '@kernel/config/database.js'
export { getConfig } from '@kernel/config/env.js'
export {
  loadNexusConfig,
  discoverPlugins,
  discoverModules,
  isPluginManifest,
  isModuleManifest,
  extractPluginManifest,
  extractModuleManifests
} from '@kernel/config/load-config.js'
export type { ResolvedConfig, ServeSPAOptions, ServeSPAFunction, SpaEntry, CaslRulesFunction } from '@kernel/config/types.js'
export type { HonoApp, NexusEnv, NexusContext, NexusMiddleware } from './http/index.js'

// Module system (engine)
export { registerModule, registerPlugin, getModules, getModule, getOrderedModules } from '@kernel/engine/index.js'
export { getRegisteredSubjects, isValidSubject, getPlugin, getPlugins, loadCoreModules } from '@kernel/engine/index.js'
export { getCoreModules, getUserModules, getCoreManifest, getUserManifest, hasUserApp, hasModule, hasPlugin } from '@kernel/engine/index.js'
export type { AppManifest } from '@kernel/engine/index.js'

// Runtime - Auto-generated services
export {
  createEntityService,
  createModuleServices,
  getServiceKey
} from './runtime/index.js'
export type { EntityService, EntityQuery } from './runtime/index.js'
export type { EntityController, EntityHandler } from './http/controllers/types.js'
export type { EntityRuntime } from './http/entity-factory.js'
// Service classes (for extension)
export { BaseEntityService } from './runtime/index.js'
export { CollectionService } from './runtime/index.js'
export { SingleService } from './runtime/index.js'
export { ReferenceService } from './runtime/index.js'
export { EventService } from './runtime/index.js'
export { ConfigService } from './runtime/index.js'
/** @deprecated Use CollectionService with retention instead */
export { TempService } from './runtime/index.js'
export { ViewService } from './runtime/index.js'
export { ExternalService } from './runtime/index.js'
/** @deprecated Use ComputedService with sources instead */
export { ComputedService as VirtualService } from './runtime/index.js'
export { ComputedService } from './runtime/index.js'
// TreeService and DagService are exported directly to avoid circular dependency issues
export { TreeService } from '@kernel/runtime/services/tree.service.js'
export { DagService } from '@kernel/runtime/services/dag.service.js'
export type {
  ModuleManifest,
  ModuleContext,
  EntityDefinition,
  ModuleRequirements,
  ModuleMiddlewares,
  ModuleAbilities,
  ContextHelpers,
  ValidateSchemas,
  PluginManifest,
  Category,
  Request,
  Response,
  NextFunction,
  RequestHandler,
  Router,
  CookieOptions
} from '@gzl10/nexus-sdk'

// Pagination types (from SDK)
export type { PaginatedResult, PaginationParams } from '@gzl10/nexus-sdk'
// Re-export AuthRequest from SDK for authenticated requests
export type { AuthRequest } from '@gzl10/nexus-sdk'

// Entity models
export type { User, Role, UserWithoutPassword, UserWithRoles, UserPresence, RoleWithCounts } from './modules/users/users.types.js'
export type { RefreshToken } from './modules/auth/auth.types.js'
export type { AuditService, AuditEntry, AuditQuery, AuditLogRow } from './modules/audit/audit.types.js'
export type { JwtPayload, TokenPair } from './modules/auth/jwt.utils.js'
export type { SchedulesApi } from './modules/schedules/schedules.types.js'

// CASL abilities
export { defineAbilityFor, packRules, unpackRules } from '@kernel/abilities/ability.factory.js'
export type { AppAbility, Actions, Subjects, SubjectStrings, SubjectRegistry } from '@kernel/abilities/ability.types.js'

// Events Hub
export { nexusEvents } from '@kernel/events/emitter.js'
export type { NexusEvents, NexusEventName, NexusEventPayload, DbEventPayload } from '@kernel/events/emitter.js'
export { eventBridge } from '@kernel/events/event-bridge.js'
export { entityRoom, isEntityRoom, parseEntityRoom } from '@kernel/events/room-utils.js'

// Socket.IO
export {
  getIO,
  initSocketIO,
  isSocketIOInitialized,
  getConnectedUsers,
  isUserConnected,
  getUserSocketCount,
  closeSocketIO
} from '@kernel/events/socket.js'

// Paths (rutas de librería y proyecto)
export { getLibPath, getProjectPath, findEnvFile } from '@kernel/config/paths.js'

// OIDC utilities - re-export from SDK for backwards compatibility
export {
  createOidcClient,
  getOidcClient,
  createStateManager,
  checkAllowedDomain,
  assertAllowedDomain
} from '@gzl10/nexus-sdk'
export type {
  OidcClient,
  StateManager,
  OidcDiscoveryDocument,
  OidcTokens,
  OidcUserInfo,
  IdTokenClaims,
  AuthorizationParams,
  TokenExchangeParams,
  TokenValidationConfig,
  OidcState,
  SessionResult,
  DomainFilterOptions,
  DomainFilterResult
} from '@gzl10/nexus-sdk'

// Auth Plugin Base - migrated to @gzl10/nexus-plugin-auth-providers/shared
// Import from '@gzl10/nexus-plugin-auth-providers/shared' for auth plugin types and factories

// =============================================================================
// MIDDLEWARE & UTILITIES (new API)
// =============================================================================

// Middleware (Hono-native)
export { requireAbility } from './http/middleware/ability.middleware.js'
export { createRateLimit, type RateLimitOptions } from './http/middleware/rate-limit.middleware.js'
export { validateBody as validate } from './http/middleware/validate.middleware.js'
export { requireNexusClient, createNexusClientMiddleware, NEXUS_CLIENT_HEADER } from './http/middleware/nexus-client.middleware.js'

// Logger
export { logger, createChildLogger } from '@kernel/logger/index.js'

// SSE (Hono)
export { streamSse } from './http/sse.js'
export { createBatchReporter, type BatchReporter } from './core/sse/batch-reporter.js'

// Error classes
export {
  AppError,
  NotFoundError,
  UnauthorizedError,
  ForbiddenError,
  ConflictError,
  ValidationError,
  ErrorCodes
} from './core/errors/app-error.js'
export type { ErrorCode, AppErrorParams, ValidationDetail } from './core/errors/app-error.js'

// DB utilities (for plugins that need filter helpers)
export { applyFilters } from '@kernel/db/filter-helpers.js'

// CLI entry point: use src/main.ts
